// Starter Tape progress — a learner's saved takes and tape completions for
// the /starter-tape studio. Pure data + functions only (no React, no
// localStorage) so it can be unit tested; the page owns reading from and
// writing to storage.
//
// Progress is plain JSON:
//   { completed: { [tapeId]: true }, shelf: [take, ...] }
// A take is a saved loop: { id, tapeId, title, song, met, savedAt }. `tapeId`
// is null when the take was made in the sandbox rather than on a tape. The
// shelf is newest first.

import { isValidSong } from "./starterTape";
import { SIDE_A, getTape } from "./starterTapeCurriculum";

// How many takes the shelf keeps before the oldest ones fall off.
export const MAX_SHELF = 60;

export const emptyProgress = () => ({ completed: {}, shelf: [] });

// Loose shape check for progress read back from storage; anything that
// fails it is ignored and the studio falls back to an empty progress.
export const isValidProgress = (p) =>
  !!p &&
  typeof p === "object" &&
  !!p.completed &&
  typeof p.completed === "object" &&
  !Array.isArray(p.completed) &&
  Object.values(p.completed).every((v) => typeof v === "boolean") &&
  Array.isArray(p.shelf) &&
  p.shelf.every(
    (t) =>
      t &&
      typeof t.id === "string" &&
      typeof t.title === "string" &&
      typeof t.met === "boolean" &&
      Number.isFinite(t.savedAt) &&
      (t.tapeId === null || typeof t.tapeId === "string") &&
      isValidSong(t.song)
  );

// A tape is unlocked once the tape it `requires` is completed (or always,
// when it requires nothing). An unknown id is never unlocked.
export const isUnlocked = (progress, tapeId) => {
  const tape = getTape(tapeId);
  if (!tape) return false;
  if (!tape.requires) return true;
  return !!progress.completed[tape.requires];
};

// The sandbox opens once the "home" tape (the third tape) is completed, so
// it stays reachable early even as more tapes are added after it.
export const isSandboxUnlocked = (progress, tapeId = "home") =>
  !!progress.completed[tapeId];

// Save a take to the top of the shelf, trimming the oldest ones past
// MAX_SHELF. Marks the tape complete when the take meets its must, and
// never un-completes a tape already marked done.
export const saveTake = (
  progress,
  { id, tapeId = null, title, song, met = false, savedAt }
) => {
  const take = { id, tapeId, title, song, met, savedAt };
  const shelf = [take, ...progress.shelf].slice(0, MAX_SHELF);
  const completed =
    met && tapeId !== null
      ? { ...progress.completed, [tapeId]: true }
      : progress.completed;
  return { ...progress, completed, shelf };
};

export const removeTake = (progress, id) => ({
  ...progress,
  shelf: progress.shelf.filter((t) => t.id !== id),
});

// Takes for one tape (or the sandbox, when tapeId is null), newest first.
export const takesFor = (progress, tapeId) =>
  progress.shelf.filter((t) => t.tapeId === tapeId);

// The title a new take for `tape` should get: the tape's title the first
// time, then "(take n)" after. Pass null for the sandbox.
export const nextTakeTitle = (progress, tape) => {
  const tapeId = tape ? tape.id : null;
  const baseTitle = tape ? tape.title : "Sandbox loop";
  const n = takesFor(progress, tapeId).length;
  return n === 0 ? baseTitle : `${baseTitle} (take ${n + 1})`;
};

export const completedCount = (progress, tapes = SIDE_A) =>
  tapes.filter((t) => progress.completed[t.id]).length;
